import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNavKrimp } from '../../hooks/useNavKrimp';
import { BottomTabBar } from './BottomTabBar';
import { MoreSheet } from './MoreSheet';
import { isLiveRoute } from '../../utils/liveRoute';

/**
 * Mobiele navigatie: zwevende onderbalk plus het Meer-paneel.
 *
 * Wordt bewust één keer op App-niveau gemonteerd en niet vanuit `CoachLayout`.
 * Elke pagina wrapt zichzelf namelijk in een eigen CoachLayout, dus bij iedere
 * routewissel wordt die layout afgebroken en opnieuw opgebouwd. De balk zou dan
 * telkens opnieuw monteren en de actieve pil kan niet meeschuiven maar zou
 * verspringen. Op App-niveau blijft dezelfde balk in leven tussen routes.
 *
 * De open-staat leeft hier lokaal en bewust niet in `appStore`; die store is
 * persist-backed, waardoor een openstaand paneel na een herlaadbeurt weer zou
 * openklappen.
 *
 * Balk en paneel zijn broers, geen ouder/kind: het paneel animeert met een
 * transform, en een transform-ouder verankert `position: fixed`-kinderen opnieuw.
 */
export const MobileNav: React.FC = () => {
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  // De balk wijkt terug tijdens het scrollen naar beneden. Zolang het paneel openstaat
  // niet: dat legt de pagina stil, dus de balk zou blijven hangen in de stand van vlak
  // vóór het openen.
  const gekrompen = useNavKrimp(sheetOpen, pathname);

  const closeSheet = useCallback(() => setSheetOpen(false), []);

  // Navigeren (ook via de browser-terugknop) sluit het paneel. Een tik op een tab die naar
  // de huidige pagina wijst komt hier niet langs - het pad verandert dan immers niet - dus
  // die sluit het paneel zelf, via onNavigate.
  useEffect(() => {
    setSheetOpen(false);
  }, [pathname]);

  // Alleen op de coach-routes; de publieke spelerpagina's hebben eigen chrome. Het live
  // verslag is schermvullend en heeft onderin zijn eigen actiebalk.
  if (!isAuthenticated || !pathname.startsWith('/coach') || isLiveRoute(pathname)) {
    return null;
  }

  return (
    <>
      <BottomTabBar
        sheetOpen={sheetOpen}
        onToggleSheet={() => setSheetOpen((open) => !open)}
        onNavigate={closeSheet}
        moreButtonRef={moreButtonRef}
        gekrompen={gekrompen}
      />
      <MoreSheet isOpen={sheetOpen} onClose={closeSheet} moreButtonRef={moreButtonRef} />
    </>
  );
};
